/* istanbul ignore file */
/* eslint-disable max-lines */

import {StyleType} from '@wix/editor-platform-ooi-metadata-resolver';
import {createStylesParam, StyleParamType, wixColorParam, wixFontParam} from '@wix/tpa-settings';
import {AddToCartActionOption} from '@wix/wixstores-client-core/dist/es/src/constants';
import {
  AutoGridTemplateRepeatOptions,
  CategoryHeaderLayout,
  CategoryHeaderPosition,
  CategoryListHorizontalButtonBorderType,
  CategoryListHorizontalOverflow,
  CategoryListHorizontalPosition,
  CategoryListItemsToShow,
  CategoryListLayout,
  CategoryListStrategy,
  DEFAULT_AUTO_GRID_PRODUCTS_COUNT,
  DEFAULT_COLS,
  DEFAULT_MANUAL_GRID_PRODUCTS_COUNT,
  DEFAULT_PRODUCT_IMAGE_SIZE,
  DEFAULT_ROWS,
  FilterAndSortButtonStyle,
  MAX_ROWS,
  ProductBorderStyleType,
  ScrollNavigationIntervalsOption,
  SliderArrowsPlacement,
} from '../constants';
import {
  AddToCartButtonIcon,
  AddToCartButtonPlacement,
  Alignments,
  ButtonContentType,
  ButtonWidthType,
  CategoryListHeaderBehavior,
  ContentJustification,
  GridType,
  HoverType,
  ImageCarouselArrowSize,
  ImageModeId,
  ImagePlacements,
  ImagePositions,
  ImageRatioId,
  LoadMoreType,
  LongCategoryListLayout,
  NamePriceLayout,
  PaginationType,
  PriceAndDiscountLayouts,
  RibbonPlacementId,
  RibbonType,
  VerticalAlignments,
} from '../types/galleryTypes';
import {GalleryViewMode} from '../wishlist/constants';
import {IStylesParams} from './types';

const responsive = createStylesParam('responsive', {
  type: StyleParamType.Boolean,
  getDefaultValue: ({isEditorX}) => isEditorX,
});

const showAlternativeImage = createStylesParam('showAlternativeImage', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => true,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: true,
    },
  },
});

const gallery_showProductOptionsButton = createStylesParam('gallery_showProductOptionsButton', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

const galleryGridType = createStylesParam('gallery_gridType', {
  type: StyleParamType.Number,
  getDefaultValue: ({getStyleParamValue}) => (getStyleParamValue(responsive) ? GridType.AUTO : GridType.MANUAL),
  metadata: {
    type: StyleType.numberEnum,
    defaultValue: {
      type: 'static',
      value: GridType.AUTO,
    },
  },
});

export const galleryColumnsDefaultValue = ({dimensions, isMobile}) => {
  if (isMobile) {
    return 1;
  }

  if (!dimensions) {
    return;
  }

  if (typeof dimensions.width !== 'number') {
    return DEFAULT_COLS;
  }
  const componentWidth: number = dimensions.width;
  const PRODUCT_ITEM_MIN_WIDTH: number = 250;
  const defaultColumns = Math.round(componentWidth / PRODUCT_ITEM_MIN_WIDTH);
  return Math.min(defaultColumns, DEFAULT_COLS);
};

const galleryColumns = createStylesParam('galleryColumns', {
  type: StyleParamType.Number,
  getDefaultValue: galleryColumnsDefaultValue,
  metadata: {
    type: StyleType.UNKNOWN_CssPropertyType, // TODO: editor 3 - grid columns property
    defaultValue: {
      type: 'static',
      value: DEFAULT_COLS,
    },
    mobileDefaultValue: {
      type: 'static',
      value: 1,
    },
  },
});

export const galleryRowsDefaultValue = ({getStyleParamValue}) => {
  const columnsNumber = getStyleParamValue(galleryColumns);
  if (!columnsNumber) {
    return;
  }
  const LEGACY_MAX_ITEM_PER_PAGE = 20;
  return Math.min(Math.floor(LEGACY_MAX_ITEM_PER_PAGE / columnsNumber), MAX_ROWS / 2);
};

const galleryMargin = createStylesParam('galleryMargin', {
  type: StyleParamType.Number,
  getDefaultValue: () => 10,
  metadata: {
    type: StyleType.margin,
    defaultValue: {
      type: 'static',
      value: 10,
    },
  },
});

const gallery_productMargin = createStylesParam('gallery_productMargin', {
  type: StyleParamType.Number,
  getDefaultValue: () => 0,
  metadata: {
    type: StyleType.margin,
    defaultValue: {
      type: 'static',
      value: 0,
    },
  },
});

const gallery_gapSize = createStylesParam('gallery_gapSize', {
  type: StyleParamType.Number,
  getDefaultValue: () => 20,
  metadata: {
    type: StyleType.gap,
    defaultValue: {
      type: 'static',
      value: 20,
    },
  },
});

const gallery_fixedGridProductsCount = createStylesParam('gallery_fixedGridProductsCount', {
  type: StyleParamType.Number,
  getDefaultValue: () => DEFAULT_MANUAL_GRID_PRODUCTS_COUNT,
  metadata: {
    type: StyleType.UNKNOWN_CssPropertyType, // TODO: editor 3 - grid products count property
    defaultValue: {
      type: 'static',
      value: DEFAULT_MANUAL_GRID_PRODUCTS_COUNT,
    },
  },
});

const gallery_imageMode = createStylesParam('gallery_imageMode', {
  type: StyleParamType.Number,
  getDefaultValue: () => ImageModeId.Crop,
  metadata: {
    type: StyleType.numberEnum,
    defaultValue: {
      type: 'static',
      value: ImageModeId.Crop,
    },
  },
});

const galleryImageRatio = createStylesParam('galleryImageRatio', {
  type: StyleParamType.Number,
  getDefaultValue: () => ImageRatioId._1x1,
  metadata: {
    type: StyleType.numberEnum,
    defaultValue: {
      type: 'static',
      value: ImageRatioId._1x1,
    },
  },
});

const gallery_imageWidth = createStylesParam('gallery_imageWidth', {
  type: StyleParamType.Number,
  getDefaultValue: () => 50,
  metadata: {
    type: StyleType.width,
    defaultValue: {
      type: 'static',
      value: 50,
    },
  },
});

const gallery_imageAndInfoSpacing = createStylesParam('gallery_imageAndInfoSpacing', {
  type: StyleParamType.Number,
  getDefaultValue: () => 0,
  metadata: {
    type: StyleType.gap,
    defaultValue: {
      type: 'static',
      value: 0,
    },
  },
});

const gallery_alternateImagePosition = createStylesParam('gallery_alternateImagePosition', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

const gallery_showAddToCartQuantity = createStylesParam('gallery_showAddToCartQuantity', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

const gallery_showAddToCartButton = createStylesParam('gallery_showAddToCartButton', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => false,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: false,
    },
  },
});

const gallery_titleFontStyle = createStylesParam('gallery_titleFontStyle', {
  type: StyleParamType.Font,
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_priceFontStyle = createStylesParam('gallery_priceFontStyle', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 16,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_discountNameFont = createStylesParam('gallery_discountNameFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 14,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 14,
    },
  },
});

const gallery_addToCartButtonTextFont = createStylesParam('gallery_addToCartButtonTextFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 15,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 15,
    },
  },
});

const gallery_ribbonTextFont = createStylesParam('gallery_ribbonTextFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 14,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 14,
    },
  },
});

const gallery_hoverType = createStylesParam('gallery_hoverType', {
  type: StyleParamType.Font,
  getDefaultValue: ({getStyleParamValue}) => ({
    fontStyleParam: false,
    value: getStyleParamValue(showAlternativeImage) ? HoverType.Alternate : HoverType.None,
  }),
  metadata: {
    type: StyleType.stringEnum,
    defaultValue: {
      type: 'static',
      value: HoverType.None,
    },
  },
});

const gallery_categoryHeaderLayout = createStylesParam('gallery_categoryHeaderLayout', {
  type: StyleParamType.Number,
  getDefaultValue: () => CategoryHeaderLayout.TextBelow,
  metadata: {
    type: StyleType.numberEnum,
    defaultValue: {
      type: 'static',
      value: CategoryHeaderLayout.TextBelow,
    },
  },
});

const gallery_imagePosition = createStylesParam('gallery_imagePosition', {
  type: StyleParamType.Font,
  getDefaultValue: () => ({
    fontStyleParam: false,
    value: ImagePositions.LEFT,
  }),
  metadata: {
    type: StyleType.stringEnum,
    defaultValue: {
      type: 'static',
      value: ImagePositions.LEFT,
    },
  },
});

const gallery_imagePlacement = createStylesParam('gallery_imagePlacement', {
  type: StyleParamType.Font,
  getDefaultValue: () => ({
    fontStyleParam: false,
    value: ImagePlacements.VERTICAL,
  }),
  metadata: {
    type: StyleType.stringEnum,
    defaultValue: {
      type: 'static',
      value: ImagePlacements.VERTICAL,
    },
  },
});

const gallery_categoriesBreadcrumbsFont = createStylesParam('gallery_categoriesBreadcrumbsFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 16,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_categoryNameFont = createStylesParam('gallery_categoryNameFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Heading-M', {
    size: 28,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Heading-M',
      size: 28,
    },
  },
});

const gallery_categoryDescriptionFont = createStylesParam('gallery_categoryDescriptionFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 16,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_categoryDescriptionReadMoreLinkFont = createStylesParam('gallery_categoryDescriptionReadMoreLinkFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 16,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

const gallery_categoryProductCounterFont = createStylesParam('gallery_categoryProductCounterFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 14,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 14,
    },
  },
});

const gallery_categoryProductCounterColor = createStylesParam('gallery_categoryProductCounterColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-4'),
  metadata: {
    type: StyleType.color,
    defaultValue: {
      type: 'static',
      preset: 'color-4',
    },
  },
});

const gallery_showCategoriesProductsCounter = createStylesParam('gallery_showCategoriesProductsCounter', {
  type: StyleParamType.Boolean,
  getDefaultValue: () => true,
  metadata: {
    type: StyleType.booleanValue,
    defaultValue: {
      type: 'static',
      value: true,
    },
  },
});

const gallery_filterOptionsFont = createStylesParam('gallery_filterOptionsFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 14,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 14,
    },
  },
});

const gallery_arrowBackground = createStylesParam('gallery_arrowBackground', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-5'),
  metadata: {
    type: StyleType.backgroundColor,
    defaultValue: {
      type: 'static',
      preset: 'color-5',
    },
  },
});

const gallery_categoryListLayout = createStylesParam('gallery_categoryListLayout', {
  type: StyleParamType.Number,
  inheritDesktop: false,
  getDefaultValue: ({isMobile}) => (isMobile ? CategoryListLayout.Horizontal : CategoryListLayout.Vertical),
  metadata: {
    type: StyleType.numberEnum,
    defaultValue: {
      type: 'static',
      value: CategoryListLayout.Vertical,
    },
    mobileDefaultValue: {
      type: 'static',
      value: CategoryListLayout.Horizontal,
    },
  },
});

const gallery_categoryListHorizontalBaseButtonBorderWidth = createStylesParam(
  'gallery_categoryListHorizontalBaseButtonBorderWidth',
  {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  }
);

const gallery_categoryListHorizontalBaseButtonCornerRadius = createStylesParam(
  'gallery_categoryListHorizontalBaseButtonCornerRadius',
  {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  }
);

const gallery_categoryListHorizontalBaseButtonBorderType = createStylesParam(
  'gallery_categoryListHorizontalBaseButtonBorderType',
  {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListHorizontalButtonBorderType.Full,
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: {
        type: 'static',
        value: CategoryListHorizontalButtonBorderType.Full,
      },
    },
  }
);

const gallery_priceColor = createStylesParam('gallery_priceColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-4'),
  metadata: {
    type: StyleType.color,
    defaultValue: {
      type: 'static',
      preset: 'color-4',
    },
  },
});

const gallery_categoryListHorizontalBaseButtonTextFont = createStylesParam(
  'gallery_categoryListHorizontalBaseButtonTextFont',
  {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      style: {
        bold: false,
        italic: false,
        underline: false,
      },
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
        // style: { // TODO: editor 3 - uncomment when support is added
        //   bold: false,
        //   italic: false,
        //   underline: false,
        // },
      },
    },
  }
);

const gallery_headerTextFont = createStylesParam('gallery_headerTextFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Heading-M', {
    size: 25,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Heading-M',
      size: 25,
    },
  },
});

const gallery_categoryTreeTitleFont = createStylesParam('gallery_categoryTreeTitleFont', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Heading-M', {
    size: 20,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Heading-M',
      size: 20,
    },
  },
});

const gallery_categoryTreeTitleColor = createStylesParam('gallery_categoryTreeTitleColor', {
  type: StyleParamType.Color,
  getDefaultValue: () => undefined,
  metadata: {
    type: StyleType.color,
    defaultValue: {
      type: 'static',
      preset: 'color-5', // TODO: editor 3 - verify fallback
    },
  },
});

const gallery_sortingFiltersTextColor = createStylesParam('gallery_sortingFiltersTextColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-5'),
  metadata: {
    type: StyleType.color,
    defaultValue: {
      type: 'static',
      preset: 'color-5',
    },
  },
});

const gallery_outOfStockFontStyle = createStylesParam('gallery_outOfStockFontStyle', {
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam('Body-M', {
    size: 16,
  }),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset: 'Body-M',
      size: 16,
    },
  },
});

export const baseStylesParams: IStylesParams = {
  gallery_discountNameFont,
  gallery_addToCartButtonTextFont,
  gallery_ribbonTextFont,
  gallery_priceFontStyle,
  full_width: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
  },
  gallery_productBorderStyleType: {
    type: StyleParamType.Number,
    getDefaultValue: () => ProductBorderStyleType.INLINE,
  },
  showAlternativeImage,
  gallery_showColorOptionsOnly: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_paginationDotsBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0,
      },
    },
  },
  gallery_paginationDotsOpacityAndColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 1,
      },
    },
  },
  galleryFiltersCategories: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  galleryFiltersPrice: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  galleryFiltersProductOptions: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  galleryShowFilters: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  galleryShowSort: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallerySortNameAsc: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallerySortNameDes: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallerySortNewest: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallerySortPriceAsc: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallerySortPriceDes: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallerySortRecommended: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_priceAndDiscountLayout: {
    type: StyleParamType.Number,
    getDefaultValue: () => PriceAndDiscountLayouts.HORIZONTAL_REGULAR_PRICE_AFTER_DISCOUNT,
  },
  gallery_showAddToCartButton,
  'mobile:gallery_showAddToCartButton': {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_showAddToCartButton),
  },
  gallery_addToCartButtonContentType: {
    type: StyleParamType.Number,
    getDefaultValue: () => ButtonContentType.Text,
  },
  gallery_addToCartButtonIcon: {
    type: StyleParamType.Number,
    getDefaultValue: () => AddToCartButtonIcon.Plus,
  },
  gallery_addToCartButtonIconSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 30,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 30,
      },
    },
  },
  gallery_addToCartButtonPlacement: {
    type: StyleParamType.Number,
    getDefaultValue: () => AddToCartButtonPlacement.ProductInfo,
  },
  gallery_addToCartButtonOnImageSidePadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 4,
      },
    },
  },
  gallery_addToCartButtonOnImageBottomPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
    metadata: {
      type: StyleType.paddingBottom,
      defaultValue: {
        type: 'static',
        value: 4,
      },
    },
  },
  gallery_addToCartButtonShowOnHover: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_alternateImagePosition,
  'mobile:gallery_alternateImagePosition': {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_alternateImagePosition),
  },
  gallery_showAddToCartQuantity,
  'mobile:gallery_showQuantity': {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_showAddToCartQuantity),
  },
  gallery_showDividers: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_showDigitalBadge: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showPrice: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showDiscountName: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showRibbon: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_ribbonSidePadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_ribbonTopPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.paddingTop,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_ribbonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_ribbonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_namePriceLayout: {
    type: StyleParamType.Number,
    getDefaultValue: () => NamePriceLayout.NameAbovePrice,
  },
  gallery_showProductName: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showTitle: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => undefined,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_showCategoryHeaderSection: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showCategoryHeaderImage: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({isMobile}) => !isMobile,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
      mobileDefaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_showCategoryHeaderReadMoreLink: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showCategoryHeaderName: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showCategoryHeaderDescription: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({isMobile}) => !isMobile,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
      mobileDefaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_paginationFirstLastArrows: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_showProductOptionsButton,
  'mobile:gallery_showProductOptions': {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_showProductOptionsButton),
  },
  gallery_shouldLimitProductOptions: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_productOptionSwatchesLimit: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
  },
  gallery_productDropdownListWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 100,
      },
    },
  },
  gallery_quantityBoxWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 100,
      },
    },
  },
  showQuickView: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  responsive,
  galleryColumns,
  galleryRows: {
    type: StyleParamType.Number,
    getDefaultValue: galleryRowsDefaultValue,
    metadata: {
      type: StyleType.UNKNOWN_CssPropertyType, // TODO: editor 3 - grid rows property
      defaultValue: {
        type: 'static',
        value: DEFAULT_ROWS,
      },
    },
  },
  galleryMargin,
  galleryMarginRow: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(galleryMargin),
    metadata: {
      type: StyleType.margin,
      defaultValue: {
        type: 'ref',
        ref: 'galleryMargin',
      },
    },
  },
  galleryMarginColumn: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(galleryMargin),
    metadata: {
      type: StyleType.margin,
      defaultValue: {
        type: 'ref',
        ref: 'galleryMargin',
      },
    },
  },
  'mobile:galleryMarginRow': {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
  },
  'mobile:galleryMarginColumn': {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
  },
  gallery_productMargin,
  'mobile:gallery_productMargin': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_productMargin) ?? 0,
  },
  gallery_imageMode,
  'mobile:gallery_imageMode': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_imageMode),
  },
  gallery_ribbonPlacement: {
    type: StyleParamType.Number,
    getDefaultValue: () => RibbonPlacementId.OnImage,
  },
  gallery_ribbonType: {
    type: StyleParamType.Number,
    getDefaultValue: () => RibbonType.RECTANGLE,
  },
  galleryImageRatio,
  'mobile:galleryImageRatio': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(galleryImageRatio),
  },
  'mobile:galleryMargin': {
    type: StyleParamType.Number,
    getDefaultValue: () => 10,
  },
  'mobile:galleryColumns': {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  gallery_addToCartAction: {
    type: StyleParamType.Number,
    getDefaultValue: () => AddToCartActionOption.MINI_CART,
  },
  gallery_productSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => DEFAULT_PRODUCT_IMAGE_SIZE,
  },
  gallery_productsCount: {
    type: StyleParamType.Number,
    getDefaultValue: () => DEFAULT_AUTO_GRID_PRODUCTS_COUNT,
  },
  gallery_fixedGridProductsCount,
  gallery_gapSize,
  gallery_gapSizeColumn: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_gapSize),
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_gapSize',
      },
    },
  },
  gallery_gapSizeRow: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_gapSize),
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_gapSize',
      },
    },
  },
  gallery_gridType: galleryGridType,
  gallery_loadMoreProductsType: {
    type: StyleParamType.Number,
    getDefaultValue: () => LoadMoreType.BUTTON,
  },
  gallery_loadMoreButtontextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_loadMoreButtonborderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_paginationFormat: {
    type: StyleParamType.Number,
    getDefaultValue: () => PaginationType.PAGES,
  },
  gallery_titleFontStyle,
  'mobile:gallery_headerTextFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_headerTextFont)?.size ?? 25,
  },
  'mobile:gallery_productNameFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_titleFontStyle)?.size ?? 16,
  },
  'mobile:gallery_productPriceFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_priceFontStyle)?.size ?? 16,
  },
  gallery_strikethroughPriceFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_priceFontStyle),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_priceFontStyle',
      },
    },
  },
  gallery_salePriceFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_priceFontStyle),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_priceFontStyle',
      },
    },
  },
  'mobile:gallery_discountNameFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_discountNameFont)?.size ?? 14,
  },
  'mobile:gallery_buttonTextFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_addToCartButtonTextFont)?.size ?? 15,
  },
  'mobile:gallery_ribbonTextFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_ribbonTextFont)?.size ?? 14,
  },
  gallery_imageWidth,
  'mobile:gallery_imageWidth': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_imageWidth),
  },
  gallery_imageAndInfoSpacing,
  'mobile:gallery_imageAndInfoSpacing': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_imageAndInfoSpacing),
  },
  gallery_productOptionsShowOptions: {
    type: StyleParamType.Number,
  },
  gallery_itemLinkVerticalCustomizePadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_itemLinkHorizontalCustomizePadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  'mobile:gallery_itemLinkVerticalCustomizePadding': {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  'mobile:gallery_itemLinkHorizontalCustomizePadding': {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  gallery_categoryHeaderSpaceBetweenTitleDescription: {
    type: StyleParamType.Number,
    getDefaultValue: ({isMobile}) => (isMobile ? 0 : 4),
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 4,
      },
      mobileDefaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryHeaderImageCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryDescriptionContainerCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryHeaderPosition: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryHeaderPosition.MinimizedAboveGallery,
  },
  gallery_categoryHeaderLayout,
  gallery_categoryHeaderImageMinHeight: {
    type: StyleParamType.Number,
    inheritDesktop: false,
    getDefaultValue: ({isMobile, getStyleParamValue}) => {
      if (isMobile) {
        return getStyleParamValue(gallery_categoryHeaderLayout) === CategoryHeaderLayout.TextInside ? 304 : 140;
      }
      return 280;
    },
    metadata: {
      type: StyleType.height,
      defaultValue: {
        type: 'static',
        value: 280,
      },
      mobileDefaultValue: {
        type: 'static',
        value: 140, // TODO: editor 3 - verify required heights
      },
    },
  },
  gallery_categoryDescriptionContainerBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_hoverType,
  'mobile:gallery_imageEffect': {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_hoverType),
  },
  gallery_imageCarouselArrowSize: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: ImageCarouselArrowSize.SMALL,
    }),
  },
  gallery_paginationAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: ContentJustification.CENTER,
    }),
  },
  gallery_verticalAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: VerticalAlignments.CENTER,
    }),
  },
  gallery_imagePlacement,
  'mobile:gallery_imagePlacement': {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_imagePlacement),
  },
  gallery_imagePosition,
  'mobile:gallery_imagePosition': {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_imagePosition),
  },
  gallery_autoGridTemplateRepeatOption: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: AutoGridTemplateRepeatOptions.AUTO_FIT,
    }),
  },
  gallery_alignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: Alignments.LEFT,
    }),
  },
  gallery_categoryInfoAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: ({isRTL}) => ({
      fontStyleParam: false,
      value: isRTL ? Alignments.RIGHT : Alignments.LEFT,
    }),
  },
  gallery_categoryInfoVerticalAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: VerticalAlignments.BOTTOM,
    }),
  },
  gallery_categoriesBreadcrumbsAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: ({isRTL}) => ({
      fontStyleParam: false,
      value: isRTL ? Alignments.RIGHT : Alignments.LEFT,
    }),
  },
  gallery_categoriesBreadcrumbsFont,
  gallery_showCategoriesBreadcrumbs: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showCategoryListHeader: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_categoryListHeaderBehavior: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListHeaderBehavior.GO_TO_PARENT,
  },
  gallery_longCategoryListLayout: {
    type: StyleParamType.Number,
    getDefaultValue: () => LongCategoryListLayout.SHOW_ALL,
  },
  gallery_categoryListMaxRows: {
    type: StyleParamType.Number,
    getDefaultValue: () => 10,
    metadata: {
      type: StyleType.UNKNOWN_CssPropertyType, // TODO: editor 3 - no specific StyleType for count/limit values
      defaultValue: {
        type: 'static',
        value: 10,
      },
    },
  },
  gallery_showCategoriesProductsCounter,
  gallery_showProductsCounter: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_showCategoriesProductsCounter),
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_showCategoriesProductsCounter',
      },
    },
  },
  gallery_showAppliedFilters: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_appliedFiltersTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({isMobile}) =>
      wixFontParam('Body-M', {
        size: isMobile ? 12 : 14,
      }) as any,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
      mobileDefaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 12,
      },
    },
  },
  gallery_addToCartButtonWidth: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: ButtonWidthType.STRETCH,
    }),
  },
  gallery_categoryProductCounterFont,
  gallery_productsCounterFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryProductCounterFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryProductCounterFont',
      },
    },
  },
  gallery_categoryNameFont,
  gallery_categoryDescriptionFont,
  gallery_categoryDescriptionReadMoreLinkFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  gallery_categoryProductCounterColor,
  gallery_productsCounterColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryProductCounterColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryProductCounterColor',
      },
    },
  },
  gallery_categoriesBreadcrumbsColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_categoryNameColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_categoryDescriptionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_categoryDescriptionReadMoreLinkColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_categoryDescriptionContainerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
  },
  gallery_categoryDescriptionContainerBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  'mobile:gallery_categoriesBreadcrumbsFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoriesBreadcrumbsFont)?.size ?? 16,
  },
  'mobile:gallery_categoryDescriptionFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryDescriptionFont)?.size ?? 16,
  },
  'mobile:gallery_categoryReadMoreFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) =>
      getStyleParamValue(gallery_categoryDescriptionReadMoreLinkFont)?.size ?? 16,
  },
  'mobile:gallery_categoryFilterAndSortButtonFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_filterOptionsFont)?.size ?? 16,
  },
  'mobile:gallery_categoryTitleFontSize': {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryNameFont)?.size ?? 28,
  },
  gallery_categoryTreeTitleFont,
  gallery_categoryTreeTitleColor,
  gallery_categoryMenuTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryTreeTitleFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryTreeTitleFont',
      },
    },
  },
  gallery_categoryMenuTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryTreeTitleColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryTreeTitleColor',
      },
    },
  },
  gallery_filtersTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryTreeTitleFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryTreeTitleFont',
      },
    },
  },
  gallery_filtersTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryTreeTitleColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryTreeTitleColor',
      },
    },
  },
  gallery_categoryAndFilterNamesFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  gallery_categoryAndFilterNamesColor: {
    type: StyleParamType.Color,
    getDefaultValue: () => undefined,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5', // TODO: editor 3 - verify fallback
      },
    },
  },
  gallery_filterOptionsFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_filterOptionsColor: {
    type: StyleParamType.Color,
    getDefaultValue: () => undefined,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5', // TODO: editor 3 - verify fallback, multiple possible values found
      },
    },
  },
  gallery_selectedCategoryFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      style: {
        bold: false,
        italic: false,
        underline: true,
      },
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
        // style: { // TODO: editor 3 - uncomment when support is added
        //   bold: false,
        //   italic: false,
        //   underline: true,
        // },
      },
    },
  },
  gallery_selectedCategoryColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  gallery_categoryListHeaderFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-M', {
      size: 16,
      style: {
        bold: false,
        italic: false,
        underline: false,
      },
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Heading-M',
        size: 16,
        // style: { // TODO: editor 3 - uncomment when support is added
        //   bold: false,
        //   italic: false,
        //   underline: false,
        // },
      },
    },
  },
  gallery_categoryListHeaderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_categoryListViewMoreLessFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      style: {
        bold: false,
        italic: false,
        underline: true,
      },
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
        // style: { // TODO: editor 3 - uncomment when support is added
        //   bold: false,
        //   italic: false,
        //   underline: true,
        // },
      },
    },
  },
  gallery_categoryListViewMoreLessColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_categoryHoverFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
      style: {
        bold: false,
        italic: false,
        underline: true,
      },
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
        // style: { // TODO: editor 3 - uncomment when support is added
        //   bold: false,
        //   italic: false,
        //   underline: true,
        // },
      },
    },
  },
  gallery_categoryHoverColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_clearFiltersButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_clearFiltersButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_sortingLabelFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  gallery_sortingLabelColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_sortingFiltersTextColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_sortingFiltersTextColor',
      },
    },
  },
  gallery_sortingDefaultOption: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: 'gallerySortRecommended',
    }),
  },
  gallery_categoryMobileFilterAndSortButtonStyle: {
    type: StyleParamType.Number,
    getDefaultValue: () => FilterAndSortButtonStyle.TEXT,
  },
  gallery_sortingAndFiltersFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
  },
  gallery_sortingAndFiltersButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  gallery_sortingAndFiltersButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: () => undefined,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_sortingAndFiltersButtonRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_quantityAndOptionsTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_priceBreakdownFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 12,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 12,
      },
    },
  },
  gallery_headerTextFont,
  gallery_titleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Heading-M', {
      size: 26,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Heading-M',
        size: 26,
      },
    },
  },
  gallery_subtitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 15,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 15,
      },
    },
  },
  gallery_sortingFiltersTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
      },
    },
  },
  gallery_sortingFiltersTextColor,
  gallery_outOfStockFontStyle,
  gallery_categoriesFilterAndSortButtonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  gallery_categoriesFilterAndSortButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5', // TODO: editor 3 - verify fallback
      },
    },
  },
  gallery_showCategories: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showCategoriesTitle: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showFiltersTitle: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_enableStickySidebar: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_categoryListStrategy: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListStrategy.AUTOMATICALLY,
  },
  gallery_categoryListItemsToShow: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListItemsToShow.OnlyNested,
  },
  gallery_categoryListLayout,
  gallery_showCategoryListShopAll: {
    type: StyleParamType.Boolean,
    getDefaultValue: ({getStyleParamValue}) =>
      getStyleParamValue(gallery_categoryListLayout) === CategoryListLayout.Horizontal,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false, // TODO: editor 3 - verify fallback is correct for editor 3
      },
    },
  },
  gallery_categoryListHorizontalOverflow: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListHorizontalOverflow.Scroll,
  },
  gallery_scrollNavigationIntervals: {
    type: StyleParamType.Number,
    getDefaultValue: () => ScrollNavigationIntervalsOption.SINGLE,
  },
  gallery_categoryListHorizontalSpaceBetween: {
    type: StyleParamType.Number,
    getDefaultValue: () => 8,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 8,
      },
    },
  },
  gallery_categoryListHorizontalButtonsVerticalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 6,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 6,
      },
    },
  },
  gallery_categoryListHorizontalButtonsHorizontalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 12,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 12,
      },
    },
  },
  gallery_categoryListHorizontalWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 100,
      },
    },
  },
  gallery_categoryListHorizontalPosition: {
    type: StyleParamType.Number,
    getDefaultValue: () => CategoryListHorizontalPosition.AttachedToGallery,
  },
  gallery_categoryListHorizontalAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: ContentJustification.START,
    }),
  },
  gallery_categoryListHorizontalBaseButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 0,
      },
    },
  },
  gallery_categoryListHorizontalHoverButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
        opacity: 0.1,
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_categoryListHorizontalHoverButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonTextFont,
  gallery_categoryListHorizontalHoverButtonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonTextFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonTextFont',
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonTextFont),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonTextFont',
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonBorderWidth,
  gallery_categoryListHorizontalHoverButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonBorderWidth),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonBorderWidth',
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonBorderWidth),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonBorderWidth',
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonCornerRadius,
  gallery_categoryListHorizontalHoverButtonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonCornerRadius),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonCornerRadius',
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonCornerRadius),
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonCornerRadius',
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0.3,
      },
    },
  },
  gallery_categoryListHorizontalHoverButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0.3,
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0,
      },
    },
  },
  gallery_editorViewMode: {
    type: StyleParamType.Number,
    getDefaultValue: () => GalleryViewMode.EDITOR_EMPTY_STATE,
  },
  gallery_categoryListHorizontalUnderlineColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0.3,
      },
    },
  },
  gallery_categoryListHorizontalUnderlineWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_categoryListHorizontalBaseButtonBorderType,
  gallery_categoryListHorizontalHoverButtonBorderType: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonBorderType),
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonBorderType',
      },
    },
  },
  gallery_categoryListHorizontalSelectedButtonBorderType: {
    type: StyleParamType.Number,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_categoryListHorizontalBaseButtonBorderType),
    metadata: {
      type: StyleType.numberEnum,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_categoryListHorizontalBaseButtonBorderType',
      },
    },
  },
  gallery_noProductsMessageFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 20,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 20,
      },
    },
  },
  gallery_quickViewTextFontStyle: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 14,
      },
    },
  },
  gallery_ribbonBackground: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  gallery_ribbonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  gallery_sliderArrowsPlacement: {
    type: StyleParamType.Number,
    getDefaultValue: () => SliderArrowsPlacement.Outside,
  },
  gallery_sliderShowArrowsOnHoverOnly: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_sliderInfiniteLoop: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_sliderTeaserOffset: {
    type: StyleParamType.Number,
    getDefaultValue: () => 40,
    metadata: {
      type: StyleType.margin,
      defaultValue: {
        type: 'static',
        value: 40,
      },
    },
  },
  gallery_sliderShowTeaser: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_showSliderNavigationArrows: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  gallery_showSliderPaginationDots: {
    type: StyleParamType.Boolean,
    getDefaultValue: () => false,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  gallery_stickySidebarDistanceFromTop: {
    type: StyleParamType.Number,
    getDefaultValue: () => 30,
    metadata: {
      type: StyleType.marginTop,
      defaultValue: {
        type: 'static',
        value: 30,
      },
    },
  },
  gallery_sliderArrowsSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 24,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 24,
      },
    },
  },
  gallery_paginationDotsMarginTop: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.marginTop,
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  },
  gallery_paginationDotsAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: Alignments.CENTER,
    }),
  },
  gallery_sliderNavigationArrowsSpacing: {
    type: StyleParamType.Number,
    getDefaultValue: () => 16,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 16,
      },
    },
  },
  gallery_sliderArrowsContainerBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_sliderArrowsCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_arrowBackground, // legacy arrow color in slider gallery
  gallery_sliderArrowsOpacityAndColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_arrowBackground),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_arrowBackground',
      },
    },
  },
  gallery_nameAndPriceGap: {
    type: StyleParamType.Number,
    getDefaultValue: () => 4,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 4,
      },
    },
  },
  gallery_quantityAndOptionsAdditionalSwatchesTextFont: createStylesParam(
    'gallery_quantityAndOptionsAdditionalSwatchesTextFont',
    {
      type: StyleParamType.Font,
      getDefaultValue: wixFontParam('Body-M', {
        size: 14,
      }),
      metadata: {
        type: StyleType.font,
        defaultValue: {
          type: 'static',
          preset: 'Body-M',
          size: 14,
        },
      },
    }
  ),
  gallery_quantityAndOptionsAdditionalSwatchesTextColor: createStylesParam(
    'gallery_quantityAndOptionsAdditionalSwatchesTextColor',
    {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-4'),
      metadata: {
        type: StyleType.color,
        defaultValue: {
          type: 'static',
          preset: 'color-4',
        },
      },
    }
  ),
  gallery_addToCartButtonTextColorDisabled: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
  },
  gallery_addToCartButtonBackgroundColorDisabled: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-3',
      },
    },
  },
  gallery_layoutVerticalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_layoutHorizontalPadding: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_sliderScrollBehavior: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  gallery_appliedFiltersBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_appliedFiltersBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_appliedFiltersBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_appliedFiltersFillColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0.1,
      },
    },
  },
  gallery_sortingFiltersColorBackground: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
  },

  gallery_cardCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_imageCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_titleTextColor: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_priceColor,
  gallery_salePriceColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_priceColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_priceColor',
      },
    },
  },
  gallery_strikethroughPriceColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_priceColor),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_priceColor',
      },
    },
  },
  gallery_priceBreakdownColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-4',
      },
    },
  },
  gallery_discountNameColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  gallery_dividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_dividerLength: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  },
  gallery_dividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  gallery_outOfStockTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-4',
      },
    },
  },
  gallery_outOfStockTextFontStyle: {
    type: StyleParamType.Font,
    getDefaultValue: ({getStyleParamValue}) => getStyleParamValue(gallery_outOfStockFontStyle),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'gallery_outOfStockFontStyle',
      },
    },
  },
  gallery_quantityAndOptionsBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
  },
  gallery_paginationActiveNumberTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  gallery_paginationNumbersTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  gallery_paginationNumbersTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  gallery_loadMoreButtonbackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 0,
      },
    },
  },
  gallery_loadMoreButtonborderSize: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: '1px',
    }),
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  gallery_loadMoreButtonbuttonWidth: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: '22%',
    }),
    metadata: {
      type: StyleType.width,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_loadMoreButtoncornersRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  gallery_loadMoreButtontextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
      },
    },
  },
  gallery_background: {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 0,
      },
    },
  },
  gallery_headerTextAlignment: {
    type: StyleParamType.Font,
    getDefaultValue: () => ({
      fontStyleParam: false,
      value: Alignments.START,
    }),
    metadata: {
      type: StyleType.stringEnum,
      defaultValue: {
        type: 'static',
        value: Alignments.START,
      },
    },
  },
};
